import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { Search, Eye, XCircle, Loader2,} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "react-hot-toast";
import "../styles/AdminQuote.css"; // Using same styles for consistency

const AdminClaims = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("claims").select("*").order("created_at", { ascending: false });
    if (error) toast.error("Claim Sync Failed");
    else setClaims(data || []);
    setLoading(false);
  };

  const handleStatusChange = async (id, newStatus) => {
    const toastId = toast.loading(`Updating claim to ${newStatus}...`);
    const { error } = await supabase.from("claims").update({ status: newStatus }).eq("id", id);

    if (error) {
      toast.error("Update failed", { id: toastId });
    } else {
      toast.success(`Claim moved to ${newStatus}`, { id: toastId });
      setClaims(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
      setSelectedClaim(null);
    }
  };

  const filteredClaims = claims.filter(c => {
    const matchesSearch = c.policy_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          c.claimant_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const status = c.status || 'New';
    if (statusFilter === "Active") return matchesSearch && (status === "New" || status === "Pending");
    return matchesSearch && (statusFilter === "All" || status === statusFilter);
  });

  return (
    <div className="rni-admin-container">
      <Toaster position="top-center" containerStyle={{ zIndex: 99999 }} />
      
      <header className="rni-header">
        <div className="header-left">
          <h1>Incident Claims</h1>
          <p>Review and settle reported insurance incidents</p>
        </div>
      </header>

      <div className="rni-controls">
        <div className="search-wrapper">
          <Search size={18} />
          <input placeholder="Search Policy # or Name..." onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="tabs">
          {["Active", "Approved", "Declined", "All"].map(t => (
            <button key={t} className={statusFilter === t ? "tab active" : "tab"} onClick={() => setStatusFilter(t)}>{t}</button>
          ))}
        </div>
      </div>

      <div className="table-card">
        <table className="rni-main-table">
          <thead>
            <tr>
              <th>Claimant / Policy</th>
              <th>Incident Type</th>
              <th>Status</th>
              <th style={{textAlign: 'right'}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" className="loading-state"><Loader2 className="rni-spin" /></td></tr>
            ) : filteredClaims.map(c => (
              <tr key={c.id}>
                <td className="entity-cell">
                  <strong>{c.policy_number}</strong>
                  <span>{c.claimant_name}</span>
                </td>
                <td><span className="type-pill">{c.incident_type}</span></td>
                <td><span className={`badge ${c.status?.toLowerCase() || 'new'}`}>{c.status || 'New'}</span></td>
                <td style={{textAlign: 'right'}}>
                  <div className="row-actions">
                    <button onClick={() => setSelectedClaim(c)} className="view-btn"><Eye size={14} /> View</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {selectedClaim && (
          <div className="glass-overlay" onClick={() => setSelectedClaim(null)}>
            <motion.div className="premium-modal" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={() => setSelectedClaim(null)}><XCircle size={24} /></button>
              
              <div className="modal-header-info">
                <span className="modal-subtitle">Claim Report</span>
                <h2>{selectedClaim.policy_number}</h2>
              </div>

              <div className="modal-grid">
                <div className="grid-item"><label>Claimant</label><p>{selectedClaim.claimant_name}</p></div>
                <div className="grid-item"><label>Type</label><p>{selectedClaim.incident_type}</p></div>
                <div className="grid-item"><label>Date</label><p>{new Date(selectedClaim.incident_date).toLocaleDateString()}</p></div>
              </div>

              <div className="modal-desc">
                <label>Incident Description</label>
                <div className="desc-inner">{selectedClaim.description || "No description provided."}</div>
              </div>

              <div className="modal-footer-btns">
                <button className="action-b approve" onClick={() => handleStatusChange(selectedClaim.id, 'Approved')}>Approve</button>
                <button className="action-b pending" onClick={() => handleStatusChange(selectedClaim.id, 'Pending')}>Pending</button>
                <button className="action-b decline" onClick={() => handleStatusChange(selectedClaim.id, 'Declined')}>Decline</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminClaims;